import QRCode from "qrcode";
import {
  accountSetupTutorial,
  authenticatorAppChoices,
  authenticatorScanStep,
  authenticatorManualInstructions,
  installAuthenticatorChoices,
  installAccountSetupTutorial,
} from "../components/account-setup-guide.js";
import { ui } from "../app/store.js";
import { esc } from "../domain/format.js";
import { captchaSlot, captchaHeaders, resetCaptcha } from "../components/turnstile.js";
import { legalLinks } from "../components/cookie-notice.js";
import { request } from "../services/api.js";
import { passwordScreen, installPasswordEvents } from "./password-security.js";
import {
  invitationScreen,
  installInvitationEvents,
} from "./invitation-page.js";
export { openPasswordChange } from "./password-security.js";

let enrollment = null;
let replacingAuthenticator = false;
let resendTimer;
let eventsInstalled = false;

const post = (url, body, headers = {}) =>
  request(url, { method: "POST", body: JSON.stringify(body), headers });
const repaint = () =>
  globalThis.dispatchEvent(new CustomEvent("milltech:render"));
const errorMarkup = () =>
  `<p class="auth-error" role="alert">${esc(ui.authError || "")}</p>`;
const disabled = () => (ui.authBusy ? " disabled" : "");
const emailField = (readonly = false) =>
  `<label>Work Email<input name="email" type="email" autocomplete="username" data-case="preserve" value="${esc(ui.authDraft?.email || "")}"${readonly ? " readonly" : ""} required></label>`;
const passwordField = (creating = false) =>
  `<label>${creating ? "Create Password" : "Password"}<input name="password" type="password" minlength="12" autocomplete="${creating ? "new-password" : "current-password"}" data-case="preserve" required></label>`;
const codeField = () =>
  '<label>Verification Code<input name="code" type="text" inputmode="numeric" pattern="[0-9]{6}" maxlength="6" autocomplete="one-time-code" data-case="preserve" placeholder="000000" required></label>';

function frame(title, description, content, extraClass = "", progress) {
  const steps = progress
    ? `<div class="auth-progress"><span>Step ${progress.current} Of ${progress.total} · ${esc(progress.label)}</span><progress value="${progress.current}" max="${progress.total}" aria-label="Setup Progress"></progress></div>`
    : "";
  return `<main class="auth-page"><section class="auth-card ${extraClass}"><div class="brand"><img src="/brand/milltech-mark.png" width="36" height="36" alt=""><span>Milltech</span></div><div><span class="eyebrow">${ui.workspaceMode === "live" ? "LIVE WORKSPACE" : ui.workspaceMode === "demo" ? "DEMO WORKSPACE · SAMPLE DATA" : "MILLTECH WORKSPACE"}</span><h1>${title}</h1><p>${description}</p>${steps}</div>${content}<small>${ui.workspaceMode === "live" ? "Live business records. Demo data is kept in a separate workspace." : "Sample records for practice. This workspace is separate from Live."}</small>${legalLinks()}</section></main>`;
}

function emailSetupPage() {
  if (ui.authEmailVerified) {
    return frame(
      "Create Your Owner Account",
      "Your email is verified. Choose your name and password.",
      `<form id="auth-form"><label>Your Name<input name="name" autocomplete="name" value="${esc(ui.authDraft?.name || "")}" required></label>${emailField(true)}${passwordField(true)}<label>Confirm Password<input name="passwordConfirm" type="password" minlength="12" autocomplete="new-password" data-case="preserve" required></label>${captchaSlot()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : "Create Account"}</button><button type="button" data-auth-action="change-email"${disabled()}>Verify Email Again</button></form>`,
      "",
      { current: 3, total: 3, label: "Account Details" },
    );
  }
  if (!ui.authCodeSent) {
    return frame(
      "Enter Your Owner Email",
      "Use the designated owner email to begin setting up this workspace.",
      `${accountSetupTutorial()}<form id="auth-request-code">${emailField()}${captchaSlot()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : "Send Verification Code"}</button></form>`,
      "",
      { current: 1, total: 3, label: "Owner Email" },
    );
  }
  const remaining = Math.max(
    0,
    Math.ceil(((ui.authResendAt || 0) - Date.now()) / 1000),
  );
  return frame(
    "Verify Your Email",
    "If this email is eligible, a code is on its way. Enter it within 10 minutes.",
    `<form id="auth-verify-email"><p class="auth-account" data-case="preserve">${esc(ui.authDraft?.email || "")}</p>${codeField()}<div class="captcha-resend"><span>Security check for resending a code</span>${captchaSlot()}</div>${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : "Verify Email"}</button><div class="auth-actions"><button type="button" data-auth-action="resend"${remaining || ui.authBusy ? " disabled" : ""}>${remaining ? `Resend Code In ${remaining}s` : "Resend Code"}</button><button type="button" data-auth-action="change-email"${disabled()}>Use A Different Email</button></div></form>`,
    "",
    { current: 2, total: 3, label: "Email Verification" },
  );
}

function challengePage() {
  const recovery = ui.authUseRecovery;
  return frame(
    recovery ? "Use A Recovery Code" : "Verify Your Sign-In",
    recovery
      ? "Enter one of the recovery codes saved when you set up your authenticator. Each code can be used once."
      : "Open the authenticator app you connected (Google Authenticator or Microsoft Authenticator) and enter the current six-digit code for Milltech.",
    `<form id="auth-mfa-challenge">${recovery ? '<label>Recovery Code<input name="code" autocomplete="off" data-case="preserve" spellcheck="false" autocapitalize="off" required></label>' : codeField()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : "Verify And Sign In"}</button><div class="auth-actions"><button type="button" data-auth-action="toggle-recovery"${disabled()}>${recovery ? "Use Authenticator Code" : "Use A Recovery Code"}</button><button type="button" data-auth-action="cancel-challenge"${disabled()}>Back To Sign-In</button></div></form>`,
  );
}

function enrollmentProgress(step, label) {
  return {
    current: step,
    total: 3,
    label,
  };
}

function enrollmentExit() {
  return `<button type="button" data-auth-action="${replacingAuthenticator ? "cancel-replacement" : "sign-out"}"${disabled()}>${replacingAuthenticator ? "Cancel" : "Sign Out"}</button>`;
}
function replacementCancel() {
  return replacingAuthenticator ? enrollmentExit() : "";
}

function enrollmentPage() {
  if (!enrollment) {
    return frame(
      replacingAuthenticator
        ? "Set Up A New Authenticator"
        : "Set Up Your Authenticator",
      replacingAuthenticator
        ? "Confirm your password to connect a new authenticator. Your current authenticator stays active until you verify the new one."
        : "Choose Google Authenticator or Microsoft Authenticator below. Confirm your Milltech password to connect your chosen app.",
      `${authenticatorAppChoices()}<form id="auth-mfa-enable">${passwordField()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : "Continue"}</button>${enrollmentExit()}</form>`,
      "",
      enrollmentProgress(1, "Connect Authenticator"),
    );
  }
  if (ui.authEnrollmentStep === "recovery") {
    return frame(
      replacingAuthenticator
        ? "Save Your New Recovery Codes"
        : "Save Your Recovery Codes",
      replacingAuthenticator
        ? "Keep these somewhere private. They replace your existing recovery codes after you verify the new authenticator."
        : "Save these in a password manager or a private place you can reach without your phone. Each recovery code works once if you lose access to your authenticator app.",
      `<p class="auth-help">Recovery codes are different from the changing six-digit codes in your authenticator app.</p><div class="auth-recovery"><ul data-case="preserve">${enrollment.backupCodes.map((code) => `<li><code>${esc(code)}</code></li>`).join("")}</ul></div><form id="auth-mfa-recovery"><label class="auth-checkbox"><input name="savedRecoveryCodes" type="checkbox"${enrollment.recoverySaved ? " checked" : ""} required><span>I Saved My Recovery Codes</span></label>${errorMarkup()}<button class="pri" type="submit"${!enrollment.recoverySaved || ui.authBusy ? " disabled" : ""}>Next</button><button type="button" data-auth-action="mfa-back-scan"${disabled()}>Back</button>${replacementCancel()}</form>`,
      "auth-card-wide",
      enrollmentProgress(2, "Recovery Codes"),
    );
  }
  if (ui.authEnrollmentStep === "verify") {
    return frame(
      replacingAuthenticator
        ? "Verify Your New Authenticator"
        : "Verify Your Authenticator",
      replacingAuthenticator
        ? "Enter the current six-digit Milltech code from your new authenticator app entry. Verification replaces your previous authenticator."
        : "Open your chosen authenticator app, find Milltech and enter its current six-digit code to finish setup.",
      `<p class="auth-help">If the code is about to change, wait for the next one. If a fresh code is rejected, check that your phone’s date and time are set automatically.</p><form id="auth-mfa-confirm">${codeField()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : replacingAuthenticator ? "Verify New Authenticator" : "Verify And Open Workspace"}</button><button type="button" data-auth-action="mfa-back-recovery"${disabled()}>Back</button>${replacementCancel()}</form>`,
      "",
      enrollmentProgress(3, "Finish Setup"),
    );
  }
  return frame(
    replacingAuthenticator
      ? "Connect A New Authenticator"
      : "Connect Your Authenticator",
    replacingAuthenticator
      ? "Add a new Milltech entry in your chosen authenticator app, then select Next. Your current authenticator stays active until the new one is verified."
      : "Open your chosen authenticator app on your phone and add your Milltech account using this QR code.",
    `<div class="auth-enrollment">${authenticatorAppChoices()}<ol class="auth-scan-steps">${authenticatorScanStep()}<li>Point your phone’s camera at the QR code below.</li><li>Check that a <strong>Milltech</strong> entry appears in the app, then select <strong>Next</strong> here.</li></ol><div class="auth-qr"><img src="${esc(enrollment.qr)}" width="200" height="200" alt="Authenticator enrollment QR code"></div><details><summary>Enter Setup Key Manually</summary>${authenticatorManualInstructions()}<code class="auth-secret" data-case="preserve">${esc(enrollment.secret)}</code></details>${errorMarkup()}<button class="pri" type="button" data-auth-action="mfa-next-recovery"${disabled()}>Next</button>${enrollmentExit()}</div>`,
    "auth-card-wide auth-card-scan",
    enrollmentProgress(1, "Connect Authenticator"),
  );
}

export function signInPage() {
  const invitation = invitationScreen();
  if (invitation)
    return frame(
      invitation.title,
      invitation.description,
      invitation.content,
      "",
      invitation.progress,
    );
  const password = passwordScreen();
  if (password)
    return frame(password.title, password.description, password.content);
  if (ui.authPendingMfa) return challengePage();
  if (ui.mfaEnrollmentRequired) return enrollmentPage();
  if (ui.setupRequired && ui.setupMethod === "email") return emailSetupPage();
  return frame(
    ui.setupRequired ? "Welcome To Milltech" : "Welcome Back",
    ui.setupRequired
      ? "Create your owner account to open this workspace."
      : "Sign in to your Milltech account.",
    `<form id="auth-form">${ui.setupRequired ? `<label>Your Name<input name="name" autocomplete="name" value="${esc(ui.authDraft?.name || "")}" required></label>` : ""}${ui.setupRequired && (ui.setupMethod === "key" || ui.setupProtected) ? '<label>Workspace Setup Key<input name="setupToken" type="password" autocomplete="off" data-case="preserve" required></label>' : ""}${emailField()}${passwordField(ui.setupRequired)}${captchaSlot()}${errorMarkup()}<button class="pri" type="submit"${disabled()}>${ui.authBusy ? "Please Wait…" : ui.setupRequired ? "Create Owner Account" : "Sign In"}</button>${ui.setupRequired ? "" : '<button type="button" class="auth-text-button" data-password-action="forgot">Forgot Password?</button>'}</form>${accountSetupTutorial()}`,
  );
}

function rememberIdentity(data) {
  ui.authDraft = {
    name: data.name || ui.authDraft?.name || "",
    email: data.email || ui.authDraft?.email || "",
  };
}

function busy(value) {
  ui.authBusy = value;
  for (const button of document.querySelectorAll(".auth-card button")) {
    if (value) {
      button.dataset.wasDisabled = String(button.disabled);
      button.disabled = true;
    } else if (button.dataset.wasDisabled !== undefined) {
      button.disabled = button.dataset.wasDisabled === "true";
      delete button.dataset.wasDisabled;
    }
  }
  document
    .querySelector(".auth-card")
    ?.setAttribute("aria-busy", String(value));
}

function showError(message) {
  ui.authError = message;
  const node = document.querySelector(".auth-error");
  if (node) node.textContent = message;
}

async function perform(action) {
  if (ui.authBusy) return;
  showError("");
  busy(true);
  try {
    await action();
  } catch (error) {
    showError(error.message || "The request could not be completed.");
  } finally {
    busy(false);
  }
}

function scheduleResend() {
  clearInterval(resendTimer);
  const update = () => {
    const button = document.querySelector('[data-auth-action="resend"]');
    if (!button) {
      clearInterval(resendTimer);
      return;
    }
    const remaining = Math.max(
      0,
      Math.ceil(((ui.authResendAt || 0) - Date.now()) / 1000),
    );
    button.textContent = remaining
      ? `Resend Code In ${remaining}s`
      : "Resend Code";
    button.disabled = !!remaining || !!ui.authBusy;
    if (!remaining) clearInterval(resendTimer);
  };
  update();
  resendTimer = setInterval(update, 1000);
  resendTimer.unref?.();
}

async function sendSetupCode(email, form) {
  let result;
  try {
    result = await post("/api/setup/code", { email }, captchaHeaders(form));
  } finally {
    resetCaptcha(form);
  }
  rememberIdentity({ email });
  ui.authCodeSent = true;
  ui.authEmailVerified = false;
  ui.authSetupJourney = true;
  ui.authResendAt = Date.now() + (result.resendAfter || 60) * 1000;
  ui.authBusy = false;
  repaint();
  scheduleResend();
  document.querySelector('input[name="code"]')?.focus();
}

async function verifyEmailCode(form) {
  const code = String(new FormData(form).get("code") || "").trim();
  await perform(async () => {
    const result = await post("/api/setup/verify", {
      email: ui.authDraft.email,
      code,
    });
    form.reset();
    clearInterval(resendTimer);
    ui.authEmailVerified = true;
    ui.authApprovalExpiresAt = Date.now() + (result.expiresIn || 600) * 1000;
    ui.authBusy = false;
    repaint();
    document.querySelector('input[name="name"]')?.focus();
  });
}

export async function submitSignIn(form) {
  const data = Object.fromEntries(new FormData(form));
  rememberIdentity(data);
  const creatingOwner = ui.setupRequired;
  const emailSetup = creatingOwner && ui.setupMethod === "email";
  if (
    emailSetup &&
    (!ui.authEmailVerified || Date.now() >= (ui.authApprovalExpiresAt || 0))
  ) {
    ui.authEmailVerified = false;
    ui.authCodeSent = false;
    ui.authApprovalExpiresAt = 0;
    showError(
      "Your email verification expired. Request a new code to continue.",
    );
    repaint();
    return;
  }
  if (emailSetup && data.password !== data.passwordConfirm) {
    showError("Your passwords do not match.");
    return;
  }
  await perform(async () => {
    const captcha = captchaHeaders(form);
    if (creatingOwner) {
      await post(
        "/api/setup",
        emailSetup
          ? { name: data.name, email: data.email, password: data.password }
          : data,
      );
      // Once setup succeeds, retries must sign in rather than recreate the owner.
      ui.setupRequired = false;
      if (emailSetup) ui.authSetupJourney = true;
    }
    const result = await post("/api/auth/sign-in/email", {
      email: data.email,
      password: data.password,
    }, captcha);
    if (result.twoFactorRedirect) {
      form.reset();
      ui.authPendingMfa = true;
      ui.authUseRecovery = false;
      ui.authBusy = false;
      repaint();
      document.querySelector('input[name="code"]')?.focus();
      return;
    }
    if (creatingOwner || ui.authSetupJourney) {
      const session = await request("/api/session");
      if (session.mfaEnrollmentRequired) {
        form.reset();
        openAuthenticatorEnrollment(session.securityUser);
        return;
      }
    }
    location.reload();
  });
  resetCaptcha(form);
}

async function enableAuthenticator(form) {
  if (!["google", "microsoft"].includes(ui.authenticatorApp)) {
    showError("Choose Google Authenticator or Microsoft Authenticator first.");
    return;
  }
  const password = new FormData(form).get("password");
  await perform(async () => {
    const result = await post(
      replacingAuthenticator
        ? "/api/security/authenticator/start"
        : "/api/auth/two-factor/enable",
      { password },
    );
    const secret = new URL(result.totpURI).searchParams.get("secret");
    if (!secret || !Array.isArray(result.backupCodes))
      throw new Error(
        "Authenticator setup could not be opened. Please try again.",
      );
    const qr = await QRCode.toDataURL(result.totpURI, {
      errorCorrectionLevel: "M",
      width: 240,
      margin: 2,
    });
    enrollment = {
      secret,
      backupCodes: result.backupCodes,
      qr,
      recoverySaved: false,
    };
    ui.authEnrollmentStep = "scan";
    form.reset();
    ui.authBusy = false;
    repaint();
  });
}

async function verifyAuthenticator(form) {
  const data = Object.fromEntries(new FormData(form));
  if (form.id === "auth-mfa-confirm" && !enrollment?.recoverySaved) {
    showError("Save your recovery codes before finishing setup.");
    return;
  }
  if (
    form.id === "auth-mfa-confirm" &&
    !["google", "microsoft"].includes(ui.authenticatorApp)
  ) {
    showError("Choose the authenticator app you used before verifying.");
    return;
  }
  await perform(async () => {
    const recovery = form.id === "auth-mfa-challenge" && ui.authUseRecovery;
    const replacement =
      form.id === "auth-mfa-confirm" && replacingAuthenticator;
    await post(
      replacement
        ? "/api/security/authenticator/confirm"
        : recovery
          ? "/api/auth/two-factor/verify-backup-code"
          : "/api/auth/two-factor/verify-totp",
      replacement
        ? { code: data.code.trim(), app: ui.authenticatorApp }
        : recovery
          ? {
              code: data.code.trim(),
              disableSession: false,
              trustDevice: false,
            }
          : { code: data.code.trim(), trustDevice: false },
      form.id === "auth-mfa-confirm" && !replacement
        ? { "X-Milltech-Authenticator-App": ui.authenticatorApp }
        : {},
    );
    replacingAuthenticator = false;
    enrollment = null;
    form.reset();
    location.reload();
  });
}

export function installAuthEvents(root = document) {
  if (eventsInstalled) return;
  eventsInstalled = true;
  installAuthenticatorChoices(root);
  installAccountSetupTutorial(root);
  installPasswordEvents(root);
  installInvitationEvents(root, { onEnrollment: openAuthenticatorEnrollment });
  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (!form.closest?.(".auth-card")) return;
    event.preventDefault();
    if (form.id === "auth-form") void submitSignIn(form);
    if (form.id === "auth-request-code") {
      const email = String(new FormData(form).get("email") || "").trim();
      void perform(() => sendSetupCode(email, form));
    }
    if (form.id === "auth-verify-email") void verifyEmailCode(form);
    if (form.id === "auth-mfa-recovery") {
      if (!new FormData(form).get("savedRecoveryCodes")) {
        showError("Save your recovery codes and check the confirmation first.");
        return;
      }
      enrollment.recoverySaved = true;
      ui.authEnrollmentStep = "verify";
      ui.authError = "";
      repaint();
      document.querySelector('input[name="code"]')?.focus();
    }
    if (form.id === "auth-mfa-enable") void enableAuthenticator(form);
    if (["auth-mfa-challenge", "auth-mfa-confirm"].includes(form.id))
      void verifyAuthenticator(form);
  });
  root.addEventListener("change", (event) => {
    if (event.target.name !== "savedRecoveryCodes") return;
    const form = event.target.closest("#auth-mfa-recovery");
    const button = form?.querySelector('button[type="submit"]');
    if (button) button.disabled = !event.target.checked || !!ui.authBusy;
  });
  root.addEventListener("click", (event) => {
    const action =
      event.target.closest?.("[data-auth-action]")?.dataset.authAction;
    if (!action || ui.authBusy) return;
    if (action === "resend") {
      if (Date.now() < (ui.authResendAt || 0)) return;
      void perform(() => sendSetupCode(ui.authDraft.email, document.querySelector("#auth-verify-email")));
      return;
    }
    ui.authError = "";
    if (action === "change-email") {
      clearInterval(resendTimer);
      ui.authCodeSent = false;
      ui.authEmailVerified = false;
      ui.authApprovalExpiresAt = 0;
      repaint();
    }
    if (
      ["mfa-next-recovery", "mfa-back-recovery"].includes(action) &&
      enrollment
    ) {
      ui.authEnrollmentStep = "recovery";
      repaint();
    }
    if (action === "mfa-back-scan" && enrollment) {
      ui.authEnrollmentStep = "scan";
      repaint();
    }
    if (action === "toggle-recovery") {
      ui.authUseRecovery = !ui.authUseRecovery;
      repaint();
      document.querySelector('input[name="code"]')?.focus();
    }
    if (action === "cancel-challenge") {
      ui.authPendingMfa = false;
      ui.authUseRecovery = false;
      repaint();
    }
    if (action === "cancel-replacement" && replacingAuthenticator) {
      void perform(async () => {
        await post("/api/security/authenticator/cancel", {});
        enrollment = null;
        replacingAuthenticator = false;
        location.reload();
      });
    }
    if (action === "sign-out") void perform(signOut);
  });
}

export function openAuthenticatorEnrollment(user, { replace = false } = {}) {
  enrollment = null;
  replacingAuthenticator = !!replace;
  ui.authenticatorApp = null;
  ui.authPasswordScreen = null;
  ui.securityUser = user;
  ui.mfaEnrollmentRequired = true;
  ui.authEnrollmentStep = "password";
  ui.authPendingMfa = false;
  ui.authError = "";
  ui.authBusy = false;
  ui.user = null;
  repaint();
}

export async function signOut() {
  await post("/api/auth/sign-out", {});
  enrollment = null;
  replacingAuthenticator = false;
  clearInterval(resendTimer);
  location.reload();
}
